import { useForm } from "@inertiajs/react";
import {
  Box,
  Callout,
  Flex,
  Heading,
  Separator,
  Switch,
  Text,
  Button,
} from "@radix-ui/themes";
import { AlertCircle } from "lucide-react";
import { SubmitEvent } from "react";
import UserPageShell from "@/shared/users/UserPageShell";

interface SettingsProps {
  allow_team_invites: boolean;
  errors?: string[];
}

export default function Settings({ allow_team_invites, errors }: SettingsProps) {
  const { data, setData, patch, processing } = useForm({
    user: { allow_team_invites },
  });

  const handleSubmit = (e: SubmitEvent) => {
    e.preventDefault();
    patch("/settings");
  };

  return (
    <UserPageShell title="Settings">
      {errors && errors.length > 0 && (
        <Callout.Root color="red" role="alert">
          <Callout.Icon><AlertCircle /></Callout.Icon>
          <Callout.Text>{errors.join(", ")}</Callout.Text>
        </Callout.Root>
      )}

      <section>
        <Heading mb="4">Account</Heading>
        <Flex
          justify="between"
          align="center"
          gap="6"
          py="3"
          style={{ borderBottom: "1px solid var(--gray-4)" }}
        >
          <Box>
            <Text size="2" weight="medium" as="p">Email and password</Text>
            <Text size="2" color="gray" as="p" mt="1">
              Change your email, or set or change your password.
            </Text>
          </Box>
          {/* A Devise page, so a plain anchor. */}
          <Button variant="soft" asChild>
            <a href="/users/edit">Edit</a>
          </Button>
        </Flex>
      </section>

      <Separator size="4" my="5" />

      <form onSubmit={handleSubmit}>
        <section>
          <Heading mb="4">Privacy</Heading>

          <Flex
            justify="between"
            align="start"
            gap="6"
            py="3"
            style={{ borderBottom: "1px solid var(--gray-4)" }}
          >
            <Box>
              <Text size="2" weight="medium" as="p">Allow team invites</Text>
              <Text size="2" color="gray" as="p" mt="1">
                When on, other users can find you by email and invite you to their team.
                Turn this off if you don't want to be discoverable.
              </Text>
            </Box>
            <Switch
              size="2"
              checked={data.user.allow_team_invites}
              onCheckedChange={(v) => setData("user", { ...data.user, allow_team_invites: v })}
            />
          </Flex>

          <Separator size="4" my="5" />

          <Flex justify="end">
            <Button type="submit" disabled={processing}>Save changes</Button>
          </Flex>
        </section>
      </form>
    </UserPageShell>
  );
}
